<template>
  <component
    :is="layoutComponent"
    page-title="Tanks"
    page-subtitle="Live oil & water strapping by location"
  >
    <div class="tanks-page">
      <header class="page-header">
        <div>
          <a href="#" class="back" @click.prevent="goBack">{{ route.path.startsWith('/company-man') ? '← Back to Dashboard' : '← Back to Tank Sheets' }}</a>
          <h1>Tanks</h1>
          <p>Live oil &amp; water strapping by location</p>
        </div>
        <div class="actions">
          <select v-if="locations.length" v-model="locationId" class="loc-select" @change="refresh">
            <option v-for="loc in locations" :key="loc.id" :value="String(loc.id)">{{ loc.name }}</option>
          </select>
          <button class="btn secondary" type="button" :disabled="loading" @click="refresh">Refresh</button>
          <button v-if="canCreateTanks" class="btn primary" type="button" @click="openAdd(tankForm.tank_type)">+ Add Tank</button>
        </div>
      </header>

      <p v-if="error" class="error-banner">{{ error }}</p>

      <section class="summary-grid">
        <article class="summary-card">
          <span>Total Fluid</span>
          <strong>{{ totals.fluid.toFixed(1) }} BBL</strong>
        </article>
        <article class="summary-card oil">
          <span>Oil On Hand</span>
          <strong>{{ totals.oil.toFixed(1) }} BBL</strong>
        </article>
        <article class="summary-card water">
          <span>Water On Hand</span>
          <strong>{{ totals.water.toFixed(1) }} BBL</strong>
        </article>
        <article class="summary-card">
          <span>Tanks Online</span>
          <strong>{{ tanks.length }}</strong>
        </article>
      </section>

      <section v-for="section in tankSections" :key="section.id" class="tank-section">
        <div class="section-head">
          <div>
            <div class="manifold-title">{{ section.label }}</div>
            <p class="flow-copy">{{ section.blurb }} Same style only in this row.</p>
          </div>
          <button v-if="canCreateTanks" class="btn secondary" type="button" @click="openAdd(section.id)">+ Add {{ section.shortLabel }}</button>
        </div>
        <div class="flow-strip">
          <div class="flow-line">
            <span class="line"></span>
            <span v-for="tank in section.tanks" :key="`node-${tank.id}`" class="node"></span>
          </div>
        </div>

      <section class="tank-yard" :class="section.style">
        <article
          v-if="section.style === 'commander'"
          v-for="unit in groupCommanderUnits(section.tanks)"
          :key="`sc-${unit.id}`"
          class="tank-card commander"
          :class="{ equalized: commanderEqualized(unitWithDrafts(unit).bays) }"
        >
          <div class="tank-name">
            <div>
              <span class="eyebrow">{{ section.label }}</span>
              <h3>{{ unit.name }}</h3>
            </div>
            <span class="status-dot" :class="{ stale: unit.bays.some((bay: any) => bay.current?.is_stale) }"></span>
          </div>
          <div class="label-row" @click.stop>
            <label class="label-picker">
              <span>Label</span>
              <select
                :value="commanderPrimary(unit)?.fluid_label || 'Production'"
                :disabled="savingLabelId === commanderPrimary(unit)?.id"
                @change="updateLabel(commanderPrimary(unit), ($event.target as HTMLSelectElement).value)"
              >
                <option v-for="opt in fluidLabels" :key="opt" :value="opt">{{ opt }}</option>
              </select>
            </label>
            <span class="label-chip" :class="labelClass(commanderPrimary(unit)?.fluid_label)">{{ commanderPrimary(unit)?.fluid_label || 'Production' }}</span>
          </div>
          <p class="flow-copy" :class="{ warn: commanderEqualized(unitWithDrafts(unit).bays) }">{{ commanderFlowCopy(unitWithDrafts(unit).bays) }}</p>
          <SandCommanderVisual
            :name="unit.name"
            :bays="commanderVisualBays(unitWithDrafts(unit))"
            :capacity-bbl="110"
            :fill-token="unit.bays.map((bay: any) => bay.current?.measured_at || bay.id).join('-') + draftToken(unit)"
            :width-px="560"
            @select="(i) => onCommanderSelect(unit, i)"
          />
          <div class="commander-bays">
            <button
              v-for="(bay, i) in commanderVisualBays(unitWithDrafts(unit))"
              :key="bay.id || `v-${i}`"
              type="button"
              class="bay-chip"
              :disabled="!bay.id || !canStrap"
              @click="bay.id && selectCommanderBay(unit, unit.bays.find((row: any) => Number(row.id) === Number(bay.id)) || bay)"
            >
              <span>{{ bay.name }}</span>
              <strong>{{ Number(bay.totalInches || 0).toFixed(0) }} in</strong>
              <small>{{ commanderBayBbl(bay.totalInches).toFixed(1) }} BBL{{ commanderEqualized(unitWithDrafts(unit).bays) ? ' · locked' : (i < 3 ? ' · weir 48' : ' · keep below 48') }}</small>
            </button>
          </div>
          <div class="reading-row">
            <div v-if="commanderUnitBarrels(unitWithDrafts(unit).bays).oil > 0.05">
              <span>Oil</span>
              <strong>{{ commanderUnitBarrels(unitWithDrafts(unit).bays).oil.toFixed(1) }} BBL</strong>
            </div>
            <div>
              <span>Water</span>
              <strong>{{ commanderUnitBarrels(unitWithDrafts(unit).bays).water.toFixed(1) }} BBL</strong>
            </div>
            <div>
              <span>System</span>
              <strong>{{ commanderUnitBarrels(unitWithDrafts(unit).bays).fluid.toFixed(1) }} BBL</strong>
              <small>110 at 6 ft</small>
            </div>
          </div>
          <footer>
            <span>{{ commanderPrimary(unit)?.current?.measured_at_human || 'No reading yet' }}</span>
            <div class="footer-actions" @click.stop>
              <button v-if="canStrap" type="button" class="enter-strap" @click="openReading(commanderPrimary(unit))">Enter Strap</button>
              <button
                v-if="canCreateTanks"
                type="button"
                class="delete-tank"
                :disabled="deletingId === commanderPrimary(unit)?.id"
                @click="deleteTank(commanderPrimary(unit))"
              >
                {{ deletingId === commanderPrimary(unit)?.id ? 'Deleting…' : 'Delete Unit' }}
              </button>
            </div>
          </footer>
        </article>

        <article v-for="tank in section.style === 'commander' ? [] : section.tanks" :key="tank.id" class="tank-card" :class="section.style" @click="canStrap && openReading(tank)">
          <div class="tank-name">
            <div>
              <span class="eyebrow">{{ tank.tank_type_label || section.label }}</span>
              <h3>{{ tank.name }}</h3>
            </div>
            <span class="status-dot" :class="{ stale: tank.current?.is_stale }"></span>
          </div>

          <div class="label-row" @click.stop>
            <label class="label-picker">
              <span>Label</span>
              <select
                :value="tank.fluid_label || 'Production'"
                :disabled="savingLabelId === tank.id"
                @change="updateLabel(tank, ($event.target as HTMLSelectElement).value)"
              >
                <option v-for="opt in fluidLabels" :key="opt" :value="opt">{{ opt }}</option>
              </select>
            </label>
            <span class="label-chip" :class="labelClass(tank.fluid_label)">{{ tank.fluid_label || 'Production' }}</span>
          </div>

          <FracTankVisual
            :name="tank.name"
            :capacity-bbl="Number(tank.capacity_bbl || section.capacityBbl)"
            :spill-inches="cutsFor(tank).spillInches"
            :oil-inches="cutsFor(tank).oilInches"
            :water-inches="cutsFor(tank).waterInches"
            :fill-token="tank.current?.measured_at || tank.measuredAt || tank.id"
            :width-px="section.style === 'upright' ? 240 : 220"
            :tank-style="section.style"
          />

          <div class="reading-row">
            <div>
              <span>Oil</span>
              <strong>{{ barrelsFor(tank).oil.toFixed(1) }} BBL</strong>
              <small>{{ cutsFor(tank).oilInches ? cutsFor(tank).oilInches.toFixed(1) : '—' }} in</small>
            </div>
            <div>
              <span>Water</span>
              <strong>{{ barrelsFor(tank).water.toFixed(1) }} BBL</strong>
              <small>{{ cutsFor(tank).waterInches ? cutsFor(tank).waterInches.toFixed(1) : '—' }} in</small>
            </div>
            <div>
              <span>Total</span>
              <strong>{{ barrelsFor(tank).fluid.toFixed(1) }} BBL</strong>
              <small>{{ tank.capacity_bbl }} max</small>
            </div>
          </div>

          <footer>
            <span>{{ tank.current?.measured_at_human || 'No reading yet' }}</span>
            <div class="footer-actions" @click.stop>
              <button v-if="canStrap" type="button" class="enter-strap" @click="openReading(tank)">Enter Strap</button>
              <button
                v-if="canCreateTanks"
                type="button"
                class="delete-tank"
                :disabled="deletingId === tank.id"
                @click="deleteTank(tank)"
              >
                {{ deletingId === tank.id ? 'Deleting…' : 'Delete' }}
              </button>
            </div>
          </footer>
        </article>

        <button v-if="canCreateTanks" class="add-tank-card" type="button" @click="openAdd(section.id)">
          <span>＋</span>
          <strong>Add {{ section.shortLabel }}</strong>
          <small>Keep this row the same tank style</small>
        </button>
      </section>
      </section>

      <section class="sync-panel">
        <div>
          <span class="sync-icon">↻</span>
          <div>
            <strong>Automatic Report Sync</strong>
            <p>Every saved strap reading updates Tanks and today's Tank Sheet from the same source record.</p>
          </div>
        </div>
        <div class="sync-tags">
          <span>Tanks</span><b>→</b><span>Tank Sheet</span><b>→</b><span>Daily Report</span>
        </div>
      </section>

      <div v-if="showReading" class="modal-backdrop" @click.self="closeReading">
        <form class="modal gauge-modal" @submit.prevent="saveReading">
          <div class="modal-head">
            <div>
            <span class="eyebrow">Hourly Strap Entry · {{ selectedTank?.fluid_label || 'Production' }}</span>
            <h2>{{ selectedTank?.name }}</h2>
            </div>
            <button type="button" class="x" @click="closeReading">×</button>
          </div>

          <SandCommanderVisual
            v-if="selectedKind.style === 'commander'"
            :name="commanderUnitName(selectedTank)"
            :bays="liveSelectedCommanderBays"
            :selected-index="commanderBayIndex(selectedTank)"
            :capacity-bbl="110"
            :live="true"
            :fill-token="`${selectedTank?.id}-${liveOilColumn}-${liveWater}`"
            :width-px="520"
            @select="(i) => selectedCommanderUnit && onCommanderSelect(selectedCommanderUnit, i)"
          />
          <FracTankVisual
            v-else
            :name="selectedTank?.name || 'Tank'"
            :capacity-bbl="Number(selectedTank?.capacity_bbl || 500)"
            :spill-inches="selectedSpill"
            :oil-inches="liveOilColumn"
            :water-inches="liveWater"
            :show-rung-labels="true"
            :live="true"
            :width-px="selectedKind.style === 'upright' ? 240 : 220"
            :tank-style="selectedKind.style"
          />
          <p v-if="selectedKind.style === 'commander' && liveTotal >= 48" class="warning">Equalized — last bay is at or above 48 in. All four bays are one tank and will fill together. Pump the last bay down through the filter pods.</p>
          <p v-else-if="selectedKind.style === 'commander'" class="gauge-help">Last bay pumps through the filter pods into the 400 upright. Keep it below 48 in or the weirs lock and the whole tank fills as one.</p>
          <p v-else class="gauge-help">{{ gaugeHelp }}</p>
          <p v-if="error" class="error-banner">{{ error }}</p>

          <div class="cut-tabs">
            <button type="button" :class="{ on: cutField === 'total' }" @click="cutField = 'total'">TOP OF FLUID</button>
            <button type="button" :class="{ on: cutField === 'oil' }" @click="cutField = 'oil'">ADD OIL / OIL CUT</button>
            <button type="button" :class="{ on: cutField === 'water' }" @click="cutField = 'water'">WATER CUT</button>
          </div>
          <p class="strap-read">{{ formatStrap(activeCutInches) }}</p>
          <div class="parts">
            <label>Feet<input v-model.number="parts.feet" type="number" min="0" :max="selectedMaxFeet" @input="applyParts" /></label>
            <label>Inches<input v-model.number="parts.inches" type="number" min="0" max="11" @input="applyParts" /></label>
            <label>Quarters
              <select v-model.number="parts.quarters" @change="applyParts">
                <option :value="0">0</option>
                <option :value="1">1/4</option>
                <option :value="2">1/2</option>
                <option :value="3">3/4</option>
              </select>
            </label>
          </div>

          <label>
            <span>Reading Time</span>
            <input v-model="readingForm.measured_at" type="datetime-local" required />
          </label>

          <label>
            <span>Notes</span>
            <textarea v-model="readingForm.notes" rows="3" placeholder="Optional field note"></textarea>
          </label>

          <div class="modal-actions">
            <button type="button" class="btn secondary" @click="closeReading">Cancel</button>
            <button type="submit" class="btn primary" :disabled="saving">{{ saving ? 'Saving…' : 'Save Reading' }}</button>
          </div>
        </form>
      </div>

      <div v-if="showAddTank" class="modal-backdrop" @click.self="showAddTank = false">
        <form class="modal" @submit.prevent="saveTank">
          <div class="modal-head">
            <div>
              <span class="eyebrow">Tank Battery</span>
              <h2>Add Tank</h2>
            </div>
            <button type="button" class="x" @click="showAddTank = false">×</button>
          </div>

          <label>
            <span>Tank Type</span>
            <select v-model="tankForm.tank_type" required @change="applyKind(tankForm.tank_type)">
              <option v-for="kind in tankKinds" :key="kind.id" :value="kind.id">{{ kind.label }}</option>
            </select>
          </label>
          <p class="kind-hint">{{ selectedAddKind.blurb }} Frac, upright, and Sand Commander stay in their own sections.</p>
          <label>
            <span>Tank Name</span>
            <input v-model="tankForm.name" placeholder="Tank 6" required />
          </label>

          <label>
            <span>Fluid Label</span>
            <select v-model="tankForm.fluid_label" required>
              <option v-for="opt in fluidLabels" :key="opt" :value="opt">{{ opt }}</option>
            </select>
          </label>

          <div class="two">
            <label>
              <span>Serial / Asset #</span>
              <input v-model="tankForm.serial_number" placeholder="Optional" />
            </label>
            <label>
              <span>Capacity (BBL)</span>
              <input v-model.number="tankForm.capacity_bbl" type="number" min="1" readonly />
            </label>
          </div>

          <label>
            <span>Strapping / Calibration Chart</span>
            <select v-model="tankForm.calibration_chart_id" required>
              <option value="" disabled>Select exact tank chart</option>
              <option v-for="chart in chartsForType" :key="chart.id" :value="String(chart.id)">{{ chart.name }}</option>
            </select>
          </label>

          <div class="warning">
            Use the TCP that matches this tank kind. Do not mix 500 frac and 400 upright charts.
          </div>

          <div class="modal-actions">
            <button type="button" class="btn secondary" @click="showAddTank = false">Cancel</button>
            <button type="submit" class="btn primary" :disabled="saving">{{ saving ? 'Adding…' : 'Add Tank' }}</button>
          </div>
        </form>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import CompanyManAppLayout from '@/components/layout/CompanyManAppLayout.vue'
import AppLayout from '@/components/layout/AppLayout.vue'
import FracTankVisual from '@/components/tanks/FracTankVisual.vue'
import SandCommanderVisual from '@/components/tanks/SandCommanderVisual.vue'
import { commanderBayBbl, commanderBayIndex, commanderEqualized, commanderFlowCopy, commanderPrimary, commanderUnitBarrels, commanderUnitName, commanderVisualBays, formatStrap, groupCommanderUnits, inchesFromParts, isWaterFluid, partsFromInches, tankCuts, tankKind, TANK_KINDS } from '@/components/tanks/fracTank'

const router = useRouter()
const route = useRoute()
const isFieldApi = computed(() => !route.path.startsWith('/company') || route.path.startsWith('/company-man'))
const isEmployee = isFieldApi
const fieldHome = computed(() => route.path.startsWith('/employee') ? '/employee' : route.path.startsWith('/company-man') ? '/company-man' : '')
const layoutComponent = computed(() => {
  if (route.path.startsWith('/company-man')) return CompanyManAppLayout
  if (isFieldApi.value) return AppLayout
  return CompanyAppLayout
})
const canCreateTanks = ref(false)
const canStrap = ref(true)

const locations = ref<Array<{ id: number; name: string }>>([])
const locationId = ref('')
const tanks = ref<any[]>([])
const chartOptions = ref<Array<{ id: number; name: string; tank_type?: string; tankType?: string }>>([])
const tankKinds = TANK_KINDS
const selectedTank = ref<any>(null)
const loading = ref(false)
const saving = ref(false)
const showAddTank = ref(false)
const showReading = ref(false)
const error = ref('')

const fluidLabels = ['Fresh', 'Heavy', 'Production', 'Oil', 'Packer Fluid'] as const
const savingLabelId = ref<number | null>(null)
const deletingId = ref<number | null>(null)

const tankForm = reactive({
  name: '',
  serial_number: '',
  tank_type: '500-bbl-frac',
  fluid_label: 'Production',
  calibration_chart_id: '',
  capacity_bbl: 500,
})

const readingForm = reactive({
  tank_id: '' as string | number,
  measured_at: new Date().toISOString().slice(0, 16),
  oil_inches: 0,
  water_inches: 0,
  notes: '',
})
const cutField = ref<'total' | 'oil' | 'water'>('total')
const parts = reactive({ feet: 0, inches: 0, quarters: 0 })
const commanderDrafts = reactive<Record<string, {
  oil_inches: number
  water_inches: number
  total_inches: number
  oil_column_inches: number
}>>({})

function draftKey(id: unknown) {
  return String(id ?? '')
}

function currentDraftPayload() {
  const total = Number(readingForm.oil_inches || 0)
  const water = selectedIsWater.value && liveOilColumn.value <= 0
    ? Math.max(Number(readingForm.water_inches || 0), total)
    : Number(readingForm.water_inches || 0)
  const oilCol = selectedIsWater.value && liveOilColumn.value <= 0 ? 0 : liveOilColumn.value
  return {
    oil_inches: total,
    water_inches: water,
    total_inches: total,
    oil_column_inches: oilCol,
  }
}

function snapshotCommanderDraft(id: unknown = selectedTank.value?.id) {
  if (!id || selectedKind.value.style !== 'commander') return
  commanderDrafts[draftKey(id)] = currentDraftPayload()
}

function applyCommanderDrafts(bays: any[]) {
  return (bays || []).map((bay) => {
    const draft = commanderDrafts[draftKey(bay.id)]
    if (!draft) return bay
    return {
      ...bay,
      waterInches: draft.water_inches,
      oilInches: draft.oil_column_inches,
      totalInches: draft.total_inches,
      water_inches: draft.water_inches,
      oil_column_inches: draft.oil_column_inches,
      total_inches: draft.total_inches,
      current: {
        ...(bay.current || {}),
        water_inches: draft.water_inches,
        oil_column_inches: draft.oil_column_inches,
        total_inches: draft.total_inches,
        oil_inches: draft.oil_column_inches,
      },
    }
  })
}

function unitWithDrafts(unit: { bays?: any[]; [key: string]: any }) {
  return { ...unit, bays: applyCommanderDrafts(unit?.bays || []) }
}

function draftToken(unit: { bays?: any[] }) {
  return (unit?.bays || []).map((bay: any) => {
    const draft = commanderDrafts[draftKey(bay.id)]
    return draft ? `${bay.id}:${draft.total_inches}:${draft.water_inches}` : `${bay.id}`
  }).join('|')
}

function patchLocalTank(id: unknown, draft: { water_inches: number; total_inches: number; oil_column_inches: number }) {
  const tank = tanks.value.find((row) => Number(row.id) === Number(id))
  if (!tank) return
  tank.waterInches = draft.water_inches
  tank.water_inches = draft.water_inches
  tank.oilInches = draft.oil_column_inches
  tank.oil_column_inches = draft.oil_column_inches
  tank.totalInches = draft.total_inches
  tank.total_inches = draft.total_inches
  tank.current = {
    ...(tank.current || {}),
    water_inches: draft.water_inches,
    oil_column_inches: draft.oil_column_inches,
    total_inches: draft.total_inches,
    oil_inches: draft.oil_column_inches,
  }
}

const selectedIsWater = computed(() => {
  const label = selectedTank.value?.fluid_label || selectedTank.value?.fluidLabel
  if (isWaterFluid(label)) return true
  return selectedKind.value.style === 'commander' && String(label || '').toLowerCase() !== 'oil'
})
const liveWater = computed(() => Number(readingForm.water_inches || 0))
const liveTotal = computed(() => Number(readingForm.oil_inches || 0))
const liveOilColumn = computed(() => Math.max(0, liveTotal.value - liveWater.value))
const activeCutInches = computed(() => {
  if (cutField.value === 'water') return liveWater.value
  if (cutField.value === 'oil') return liveOilColumn.value
  return liveTotal.value
})
const gaugeHelp = computed(() => {
  if (!liveTotal.value && !liveWater.value) {
    return selectedIsWater.value
      ? 'Enter the water strap. This tank is labeled water, so the fill stays blue unless you add an oil cut.'
      : 'Enter a cut — oil and water will fill this tank as you type.'
  }
  if (liveOilColumn.value <= 0) return `Water ${formatStrap(liveWater.value)}`
  return `Oil ${formatStrap(liveOilColumn.value)} on water ${formatStrap(liveWater.value)}`
})

function cutsFor(tank: any) {
  return tankCuts(tank)
}

function barrelsFor(tank: any) {
  const cuts = tankCuts(tank)
  const oil = Number(tank?.current?.oil_bbl ?? tank?.oilBbl ?? 0)
  const water = Number(tank?.current?.water_bbl ?? tank?.waterBbl ?? 0)
  const fluid = Number(tank?.current?.total_bbl ?? tank?.totalBbl ?? (oil + water))
  if (isWaterFluid(tank?.fluid_label || tank?.fluidLabel) && cuts.oilInches <= 0 && cuts.waterInches > 0) {
    const allWater = fluid || water || oil
    return { oil: 0, water: allWater, fluid: allWater }
  }
  return { oil, water, fluid }
}

const tankSections = computed(() =>
  TANK_KINDS.map((kind) => ({
    ...kind,
    tanks: tanks.value.filter((tank) => tankKind(tank.tank_type || tank.tankType).id === kind.id),
  })),
)

const selectedKind = computed(() => tankKind(selectedTank.value?.tank_type || selectedTank.value?.tankType))
const selectedCommanderUnit = computed(() => {
  const tank = selectedTank.value
  if (!tank || selectedKind.value.style !== 'commander') return null
  return groupCommanderUnits(tanks.value).find((unit) => unit.bays.some((bay: any) => Number(bay.id) === Number(tank.id))) || null
})
const liveSelectedCommanderBays = computed(() => {
  const unit = selectedCommanderUnit.value
  if (!unit) return []
  return commanderVisualBays(unitWithDrafts(unit))
})
const selectedSpill = computed(() => cutsFor(selectedTank.value).spillInches || selectedKind.value.spillInches)
const selectedMaxFeet = computed(() => Math.floor(selectedSpill.value / 12))
const selectedAddKind = computed(() => tankKind(tankForm.tank_type))
const chartsForType = computed(() =>
  chartOptions.value.filter((chart) => {
    const type = chart.tank_type || chart.tankType
    return type === tankForm.tank_type
  }),
)

function applyKind(type: string) {
  const kind = tankKind(type)
  tankForm.tank_type = kind.id
  tankForm.capacity_bbl = kind.capacityBbl
  if (kind.style === 'commander' && (tankForm.fluid_label === 'Production' || !tankForm.fluid_label)) {
    tankForm.fluid_label = 'Fresh'
  }
  const match = chartOptions.value.find((chart) => (chart.tank_type || chart.tankType) === kind.id)
  tankForm.calibration_chart_id = match ? String(match.id) : ''
}

function onCommanderSelect(unit: { bays: any[] }, index: number) {
  const visual = commanderVisualBays(unitWithDrafts(unit))[index]
  const bay = unit.bays.find((row: any) => Number(row.id) === Number(visual?.id)) || (visual?.id ? visual : null)
  if (bay?.id) selectCommanderBay(unit, bay)
}

async function persistCommanderDraft(id: unknown) {
  const draft = commanderDrafts[draftKey(id)]
  if (!draft || !id) return
  const url = isEmployee.value ? `/api/employee/tanks/${id}/readings` : `/api/company/tanks/${id}/readings`
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      measured_at: readingForm.measured_at,
      notes: readingForm.notes,
      oil_inches: draft.oil_inches,
      water_inches: draft.water_inches,
      total_inches: draft.total_inches,
      oil_column_inches: draft.oil_column_inches,
    }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
  patchLocalTank(id, draft)
}

async function selectCommanderBay(unit: { bays: any[] }, bay: any) {
  if (!bay?.id || !canStrap.value) return
  snapshotCommanderDraft()
  if (showReading.value && selectedTank.value?.id && Number(selectedTank.value.id) !== Number(bay.id)) {
    try {
      await persistCommanderDraft(selectedTank.value.id)
    } catch (e: any) {
      error.value = e.message || 'Could not keep that bay strap'
    }
  }
  openReading(bay)
}

async function closeReading() {
  snapshotCommanderDraft()
  const id = selectedTank.value?.id
  showReading.value = false
  if (id && commanderDrafts[draftKey(id)]) {
    try {
      await persistCommanderDraft(id)
    } catch {
      // Keep the on-screen draft if the write fails.
    }
  }
}

function openAdd(type: string) {
  applyKind(type)
  showAddTank.value = true
}

const totals = computed(() => {
  return tanks.value.reduce(
    (acc, t) => {
      const bbl = barrelsFor(t)
      acc.oil += bbl.oil
      acc.water += bbl.water
      acc.fluid += bbl.fluid
      return acc
    },
    { oil: 0, water: 0, fluid: 0 },
  )
})

function authHeaders(json = true) {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
  }
  if (json) headers['Content-Type'] = 'application/json'
  return headers
}

async function loadLocations() {
  if (isEmployee.value) {
    const res = await fetch('/api/employee/tanks', { headers: authHeaders() })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    locations.value = data.locations || []
    locationId.value = String(data.locationId || locations.value[0]?.id || '')
    if (typeof data.canCreateTanks === 'boolean') canCreateTanks.value = data.canCreateTanks
    if (typeof data.canStrap === 'boolean') canStrap.value = data.canStrap
    return
  }
  const res = await fetch('/api/company/locations', { headers: authHeaders() })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const data = await res.json()
  locations.value = data.locations || []
  if (!locationId.value && locations.value.length) {
    locationId.value = String(locations.value[0].id)
  }
}

async function loadCharts() {
  const url = isEmployee.value ? '/api/employee/tanks' : '/api/company/tank-calibration-charts'
  const res = await fetch(url, { headers: authHeaders() })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const data = await res.json()
  chartOptions.value = data.charts || []
  if (typeof data.canCreateTanks === 'boolean') canCreateTanks.value = data.canCreateTanks
  if (typeof data.canStrap === 'boolean') canStrap.value = data.canStrap
  applyKind(tankForm.tank_type)
}

async function refresh() {
  if (!locationId.value) return
  loading.value = true
  error.value = ''
  try {
    const url = isEmployee.value
      ? `/api/employee/tanks?location_id=${locationId.value}`
      : `/api/company/locations/${locationId.value}/tanks`
    const res = await fetch(url, { headers: authHeaders() })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    tanks.value = isEmployee.value
      ? (data.tanks || []).map((tank: any) => ({
          ...tank,
          capacity_bbl: tank.capacityBbl,
          fluid_label: tank.fluidLabel || 'Production',
          tank_type: tank.tankType,
          tank_type_label: tank.tankTypeLabel,
          max_height_inches: tank.maxHeightInches,
          serial_number: tank.serialNumber,
          tank_group: tank.tankGroup,
          current: {
            oil_bbl: tank.oilBbl,
            water_bbl: tank.waterBbl,
            total_bbl: tank.totalBbl,
            oil_inches: tank.oilColumnInches ?? tank.oilInches,
            oil_column_inches: tank.oilColumnInches ?? tank.oilInches,
            water_inches: tank.waterInches,
            total_inches: tank.totalInches,
            measured_at_human: tank.measuredAtHuman,
          },
        }))
      : (data.tanks || [])
  } catch (e: any) {
    error.value = e.message || 'Failed to load tanks'
  } finally {
    loading.value = false
  }
}

watch(cutField, () => {
  Object.assign(parts, partsFromInches(activeCutInches.value))
})

function applyParts() {
  const next = inchesFromParts(parts.feet, parts.inches, parts.quarters)
  const water = Number(readingForm.water_inches || 0)
  const total = Number(readingForm.oil_inches || 0)
  const waterTank = selectedIsWater.value
  if (cutField.value === 'total') {
    if (waterTank && liveOilColumn.value <= 0) {
      readingForm.oil_inches = next
      readingForm.water_inches = next
    } else {
      readingForm.oil_inches = Math.max(water, next)
    }
  } else if (cutField.value === 'water') {
    readingForm.water_inches = next
    if (next > total || (waterTank && liveOilColumn.value <= 0)) readingForm.oil_inches = next
  } else {
    readingForm.oil_inches = water + next
  }
  snapshotCommanderDraft()
}

function openReading(tank: any) {
  selectedTank.value = tank
  const draft = commanderDrafts[draftKey(tank.id)]
  const cuts = draft
    ? { totalInches: draft.total_inches, waterInches: draft.water_inches }
    : tankCuts(tank)
  readingForm.tank_id = tank.id
  readingForm.measured_at = readingForm.measured_at || new Date().toISOString().slice(0, 16)
  if (!readingForm.measured_at) readingForm.measured_at = new Date().toISOString().slice(0, 16)
  readingForm.oil_inches = cuts.totalInches
  readingForm.water_inches = cuts.waterInches
  cutField.value = selectedIsWater.value || isWaterFluid(tank.fluid_label || tank.fluidLabel) ? 'water' : 'total'
  Object.assign(parts, partsFromInches(cutField.value === 'water' ? cuts.waterInches : cuts.totalInches))
  snapshotCommanderDraft(tank.id)
  showReading.value = true
}

async function saveTank() {
  saving.value = true
  error.value = ''
  try {
    const res = await fetch(
      isEmployee.value ? '/api/employee/tanks' : `/api/company/locations/${locationId.value}/tanks`,
      {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...tankForm, location_id: Number(locationId.value) }),
      },
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    const createdId = Number(data.id || data.tank?.id || 0)
    tankForm.name = ''
    tankForm.serial_number = ''
    tankForm.fluid_label = 'Production'
    applyKind(tankForm.tank_type)
    showAddTank.value = false
    await refresh()
    const created = tanks.value.find((tank) => Number(tank.id) === createdId)
    if (created && canStrap.value && tankKind(created.tank_type || created.tankType).style !== 'commander') {
      openReading(created)
    }
  } catch (e: any) {
    error.value = e.message || 'Could not add tank'
  } finally {
    saving.value = false
  }
}

async function saveReading() {
  saving.value = true
  error.value = ''
  try {
    snapshotCommanderDraft()
    const unit = selectedCommanderUnit.value
    const ids = unit?.bays?.length
      ? [...unit.bays].sort((a: any, b: any) => commanderBayIndex(a) - commanderBayIndex(b)).map((bay: any) => bay.id)
      : [readingForm.tank_id]
    for (const id of ids) {
      if (commanderDrafts[draftKey(id)] || Number(id) === Number(readingForm.tank_id)) {
        if (!commanderDrafts[draftKey(id)] && Number(id) === Number(readingForm.tank_id)) snapshotCommanderDraft(id)
        await persistCommanderDraft(id)
      }
    }
    showReading.value = false
    await refresh()
  } catch (e: any) {
    error.value = e.message || 'Could not save reading'
  } finally {
    saving.value = false
  }
}

async function deleteTank(tank: any) {
  const label = tank.fluid_label ? ` (${tank.fluid_label})` : ''
  const unit = tankKind(tank.tank_type || tank.tankType).style === 'commander'
  if (!window.confirm(unit
    ? `Delete ${commanderUnitName(tank)}? Readings for this 110 BBL system will be removed.`
    : `Delete ${tank.name}${label}? Readings for this tank will be removed.`)) {
    return
  }
  deletingId.value = tank.id
  error.value = ''
  try {
    const res = await fetch(isEmployee.value ? `/api/employee/tanks/${tank.id}` : `/api/company/tanks/${tank.id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    if (selectedTank.value?.id === tank.id) {
      showReading.value = false
      selectedTank.value = null
    }
    await refresh()
  } catch (e: any) {
    error.value = e.message || 'Could not delete tank'
  } finally {
    deletingId.value = null
  }
}

async function updateLabel(tank: any, label: string) {
  if (!label || label === tank.fluid_label) return
  savingLabelId.value = tank.id
  error.value = ''
  try {
    const res = await fetch(
      isEmployee.value ? `/api/employee/tanks/${tank.id}` : `/api/company/tanks/${tank.id}`,
      {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ fluid_label: label }),
      },
    )
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    tank.fluid_label = label
  } catch (e: any) {
    error.value = e.message || 'Could not update label'
    await refresh()
  } finally {
    savingLabelId.value = null
  }
}

function labelClass(label?: string) {
  const key = (label || 'Production').toLowerCase().replace(/\s+/g, '-')
  return `label-${key}`
}

function fluidHeightPct(tank: any) {
  const cap = Number(tank.capacity_bbl || 500)
  return Math.max(0, Math.min(100, (Number(tank.current?.total_bbl || 0) / cap) * 100))
}

function waterHeightPct(tank: any) {
  const total = Number(tank.current?.total_bbl || 0)
  if (!total) return 0
  return Math.max(0, Math.min(100, (Number(tank.current?.water_bbl || 0) / Number(tank.capacity_bbl || 500)) * 100))
}

function oilHeightPct(tank: any) {
  const total = Number(tank.current?.total_bbl || 0)
  if (!total) return 0
  return Math.max(0, fluidHeightPct(tank) - waterHeightPct(tank))
}

function goBack() {
  if (route.path.startsWith('/company-man')) {
    router.push('/company-man')
    return
  }
  router.push(isEmployee.value ? `${fieldHome.value}/tank-sheets` || '/tank-sheets' : '/company/tank-sheets')
}

onMounted(async () => {
  try {
    await Promise.all([loadLocations(), loadCharts()])
    await refresh()
  } catch (e: any) {
    error.value = e.message || 'Failed to load tanks module'
  }
})
</script>

<style scoped>
.tanks-page{padding:8px 0 28px;color:#dbe8f5;font-family:Inter,system-ui,sans-serif}
.page-header{display:flex;align-items:end;justify-content:space-between;gap:16px;margin-bottom:18px}
.page-header h1{margin:6px 0 2px;font-size:28px}.page-header p,.back{color:#819ab3;font-size:13px}.back{text-decoration:none}
.actions,.modal-actions{display:flex;gap:10px;flex-wrap:wrap;align-items:center}
.loc-select{background:#223248;border:1px solid #31465f;border-radius:8px;color:#eaf3fa;padding:9px 12px}
.btn{border:1px solid #31465f;border-radius:8px;padding:9px 14px;font-weight:800;cursor:pointer}.primary{background:#087b67;color:#fff;border-color:#0d967d}.secondary{background:#223248;color:#eaf3fa}
.error-banner{margin:0 0 12px;padding:10px 14px;border-radius:8px;background:#411b23;border:1px solid #8f3c49;color:#ffc4cc}
.summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:14px}.summary-card{background:#18263a;border:1px solid #2c4058;border-radius:10px;padding:14px}.summary-card span{color:#8197ae;font-size:12px}.summary-card strong{display:block;margin-top:4px;font-size:22px}.summary-card.oil strong{color:#f3b75e}.summary-card.water strong{color:#51c4e8}
.tank-section{margin-bottom:22px}.section-head{display:flex;justify-content:space-between;align-items:end;gap:12px;margin-bottom:8px}.kind-hint{margin:-4px 0 8px;font-size:12px;color:#7f96ad}
.flow-strip{background:#152337;border:1px solid #2a3e56;border-radius:10px;padding:12px 16px;margin-bottom:14px}.manifold-title{font-weight:800;font-size:13px}.flow-line{display:flex;align-items:center;justify-content:space-around;position:relative;height:22px}.line{position:absolute;left:2%;right:2%;height:4px;background:#3c9d8e;border-radius:99px}.node{z-index:1;width:13px;height:13px;background:#0fb99d;border:3px solid #1a2b40;border-radius:50%}.flow-copy{font-size:11px;color:#728aa2}.flow-copy.warn{color:#e7c36a}.tank-card.equalized{border-color:#c9a227}
.tank-yard{display:grid;grid-template-columns:repeat(5,minmax(210px,1fr));gap:14px;align-items:start}.tank-yard.commander{grid-template-columns:1fr}.tank-card,.add-tank-card{background:#18263a;border:1px solid #2b425b;border-radius:12px;padding:12px;color:inherit}.tank-card{cursor:pointer}.tank-card:hover{border-color:#159d86}.tank-card.commander{cursor:pointer}.commander-bays{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-top:10px}.bay-chip{background:#111e2f;border:1px solid #31465f;border-radius:8px;padding:8px;color:#eaf3fa;text-align:left;cursor:pointer}.bay-chip span{display:block;font-size:10px;color:#7f96ad}.bay-chip strong{display:block;margin:3px 0 1px}.bay-chip small{color:#708aa2;font-size:10px}.tank-name{display:flex;justify-content:space-between;align-items:start}.eyebrow{font-size:10px;color:#6f8aa5;text-transform:uppercase;letter-spacing:.08em}.tank-name h3{margin:2px 0 10px}.status-dot{width:9px;height:9px;border-radius:50%;background:#24d4aa;box-shadow:0 0 0 4px rgba(36,212,170,.08)}.status-dot.stale{background:#f3b75e}
.label-row{display:flex;align-items:center;justify-content:space-between;gap:8px;margin:-2px 0 10px}
.label-picker{display:grid;gap:3px;flex:1;min-width:0}
.label-picker span{font-size:9px;color:#6f8aa5;text-transform:uppercase;letter-spacing:.06em}
.label-picker select{width:100%;background:#111e2f;border:1px solid #31465f;border-radius:7px;color:#eaf3fa;padding:6px 8px;font-size:12px;font-weight:700}
.label-chip{flex-shrink:0;border-radius:999px;padding:4px 9px;font-size:10px;font-weight:800;letter-spacing:.03em;text-transform:uppercase}
.label-fresh,.label-water{background:rgba(56,189,248,.16);color:#7dd3fc;border:1px solid rgba(56,189,248,.35)}
.label-heavy{background:rgba(168,85,247,.16);color:#d8b4fe;border:1px solid rgba(168,85,247,.35)}
.label-production{background:rgba(52,211,153,.16);color:#6ee7b7;border:1px solid rgba(52,211,153,.35)}
.label-oil{background:rgba(245,158,11,.16);color:#fbbf24;border:1px solid rgba(245,158,11,.35)}
.label-packer-fluid{background:rgba(148,163,184,.16);color:#cbd5e1;border:1px solid rgba(148,163,184,.35)}
.tank-shell{height:250px;position:relative;border:4px solid #455466;border-radius:8px 8px 20px 20px;background:#202d3d;overflow:hidden;box-shadow:inset 0 0 0 2px #111c29}.tank-shell:before{content:"";position:absolute;left:8px;right:8px;top:10px;height:8px;background:repeating-linear-gradient(90deg,#536679 0 18px,#33465a 18px 34px);z-index:4}.level-mask{position:absolute;inset:22px 7px 10px;overflow:hidden;border-radius:2px 2px 12px 12px}.water-level{position:absolute;left:0;right:0;bottom:0;background:linear-gradient(#34bddd,#187ba5)}.oil-level{position:absolute;left:0;right:0;background:linear-gradient(#d49a3c,#946226)}.ruler{position:absolute;right:8px;top:30px;bottom:14px;display:flex;flex-direction:column;justify-content:space-between;font-size:8px;color:#dfe8ee;z-index:5}.tank-label{position:absolute;top:42%;left:50%;transform:translate(-50%,-50%);z-index:6;font-size:18px;font-weight:900;color:rgba(255,255,255,.82)}.tank-valve{position:absolute;left:50%;bottom:-1px;transform:translateX(-50%);width:34px;height:13px;border:3px solid #677887;background:#253545;z-index:7}.ladder{position:absolute;left:10px;top:28px;bottom:18px;width:23px;border-left:3px solid #657687;border-right:3px solid #657687;z-index:6;display:flex;flex-direction:column;justify-content:space-around}.ladder i{height:2px;background:#657687}
.reading-row{display:grid;grid-template-columns:repeat(3,1fr);gap:5px;margin-top:10px}.reading-row>div{background:#111e2f;border-radius:7px;padding:7px}.reading-row span,.reading-row small{font-size:9px;color:#708aa2;display:block}.reading-row strong{font-size:12px}
.tank-card footer{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:10px;color:#6d86a0;font-size:10px}
.footer-actions{display:flex;align-items:center;gap:8px}
.enter-strap{background:transparent;color:#3dd3b5;border:0;font-weight:800;cursor:pointer}
.delete-tank{background:rgba(248,113,113,.08);color:#f87171;border:1px solid rgba(248,113,113,.35);border-radius:6px;padding:4px 8px;font-weight:800;cursor:pointer}
.delete-tank:disabled{opacity:.6;cursor:wait}
.add-tank-card{min-height:385px;display:flex;flex-direction:column;align-items:center;justify-content:center;border-style:dashed;cursor:pointer}.add-tank-card span{font-size:34px;color:#35c9ab}.add-tank-card small{margin-top:5px;color:#718ba4}
.sync-panel{margin-top:14px;background:#132c31;border:1px solid #176657;border-radius:10px;padding:12px 15px;display:flex;justify-content:space-between;align-items:center;gap:16px}.sync-panel>div:first-child{display:flex;align-items:center;gap:10px}.sync-icon{font-size:28px;color:#36d3b2}.sync-panel p{margin:3px 0 0;font-size:11px;color:#76a79d}.sync-tags{display:flex;gap:7px;align-items:center;font-size:11px}.sync-tags span{background:#193d3c;border:1px solid #256e61;padding:6px 8px;border-radius:6px}
.modal-backdrop{position:fixed;inset:0;background:rgba(2,8,16,.72);display:grid;place-items:center;z-index:100;padding:20px}.modal{width:min(560px,100%);background:#17263a;border:1px solid #334b64;border-radius:14px;padding:18px;box-shadow:0 30px 80px rgba(0,0,0,.42)}.modal-head{display:flex;justify-content:space-between;align-items:start;margin-bottom:12px}.modal h2{margin:3px 0}.x{border:0;background:transparent;color:#aac0d4;font-size:28px;cursor:pointer}.modal label{display:block;margin:10px 0}.modal label span{display:block;color:#91a8be;font-size:11px;margin-bottom:5px}.modal input,.modal select,.modal textarea{width:100%;box-sizing:border-box;background:#101e30;border:1px solid #344b62;border-radius:8px;color:#e5eef6;padding:10px;outline:none}.two{display:grid;grid-template-columns:1fr 1fr;gap:10px}.calc-preview,.warning{font-size:11px;padding:10px;border-radius:8px;background:#112f35;color:#80b9ae}.warning{background:#3a2e17;color:#dec17f}.modal-actions{justify-content:flex-end;margin-top:14px}
.gauge-modal{max-height:92vh;overflow:auto;width:min(640px,100%)}
.gauge-help{color:#92A5B7;font-size:12px;margin:6px 0 10px}
.cut-tabs{display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px}
.cut-tabs button{background:#111e2f;border:1px solid #31465f;color:#92A5B7;border-radius:8px;padding:8px 6px;font-size:10px;font-weight:800;cursor:pointer}
.cut-tabs button.on{border-color:#14b8a6;color:#5eead4;background:rgba(20,184,166,.12)}
.strap-read{font-size:20px;font-weight:900;margin:8px 0}
.parts{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px}
@media(max-width:1350px){.tank-yard{grid-template-columns:repeat(3,1fr)}}@media(max-width:900px){.tank-yard{grid-template-columns:repeat(2,1fr)}.summary-grid{grid-template-columns:repeat(2,1fr)}}@media(max-width:600px){.tank-yard,.summary-grid{grid-template-columns:1fr}.page-header,.sync-panel{align-items:stretch;flex-direction:column}.two{grid-template-columns:1fr}}
</style>
